/**
 * ContactArchiveDeleteDialog
 * --------------------------
 * Unified dialog for archiving / restoring / permanently deleting a contact.
 *
 * Modes:
 *   • archive   — soft-delete (status='archived'), allowed for any firm
 *                 user, BLOCKED when there are open invoices/bills.
 *   • restore   — un-archive a previously archived contact.
 *   • delete    — permanent removal, **owner/admin only**, requires the
 *                 user to type the contact name to confirm. Allowed only
 *                 when there is zero historical activity.
 *
 * Calls `GET /api/contacts/{id}/delete-impact` on open to render an honest
 * impact summary (open invoices, total invoices, bills, notes, contacts).
 */
import { useState, useCallback } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from './ui/alert-dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Loader2, AlertTriangle, Archive, Trash2, RotateCcw, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '../fetchInterceptor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ContactArchiveDeleteDialog({
  open,
  onOpenChange,
  contact,           // { contact_id, name, contact_type, status }
  mode,              // 'archive' | 'restore' | 'delete'
  viewerIsAdmin,
  onSuccess,
}) {
  const [impact, setImpact] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [prevOpen, setPrevOpen] = useState(false);

  const isDelete = mode === 'delete';
  const isRestore = mode === 'restore';
  const isArchive = mode === 'archive';

  const fetchImpact = useCallback(async (contactId, restoreMode) => {
    if (!contactId || restoreMode) return;
    setLoading(true);
    try {
      const r = await apiFetch(`${API_URL}/api/contacts/${contactId}/delete-impact`);
      if (r.ok) setImpact(await r.json());
    } finally {
      setLoading(false);
    }
  }, []);

  // Render-body open-transition detection — same pattern as SupportPage.
  // Avoids the strict 'set-state-in-effect' rule by deferring state setters
  // into a microtask, so the actual `setState` calls run outside the
  // render/effect chain.
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) {
      queueMicrotask(() => {
        setConfirmText('');
        setImpact(null);
        fetchImpact(contact?.contact_id, isRestore);
      });
    }
  }

  const nameMatches = (confirmText || '').trim() === (contact?.name || '').trim();
  const canConfirm = isDelete ? (nameMatches && impact?.can_hard_delete) :
                     isArchive ? (impact?.can_archive ?? true) :
                     true; // restore is always allowed if status==='archived'

  const handleConfirm = async () => {
    if (!contact?.contact_id) return;
    setSubmitting(true);
    try {
      let r;
      if (isRestore) {
        r = await apiFetch(`${API_URL}/api/contacts/${contact.contact_id}/restore`, { method: 'POST' });
      } else if (isDelete) {
        r = await apiFetch(`${API_URL}/api/contacts/${contact.contact_id}?hard=true`, { method: 'DELETE' });
      } else {
        r = await apiFetch(`${API_URL}/api/contacts/${contact.contact_id}`, { method: 'DELETE' });
      }
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(data.detail || 'Action failed');
        return;
      }
      toast.success(
        isRestore ? `${contact.name} restored` :
        isDelete  ? `${contact.name} permanently deleted` :
                    `${contact.name} archived — you can restore it from the Archived tab`,
      );
      onOpenChange(false);
      onSuccess?.();
    } finally {
      setSubmitting(false);
    }
  };

  if (!contact) return null;

  const titleByMode = {
    archive: 'Archive this contact?',
    restore: 'Restore this contact?',
    delete: 'Permanently delete this contact?',
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid={`contact-${mode}-dialog`} className="max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            {isDelete && <Trash2 className="w-5 h-5 text-rose-500" />}
            {isArchive && <Archive className="w-5 h-5 text-amber-500" />}
            {isRestore && <RotateCcw className="w-5 h-5 text-emerald-500" />}
            {titleByMode[mode]}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm pt-2">
              <p>
                You&apos;re about to {isDelete ? 'permanently delete' : isArchive ? 'archive' : 'restore'}{' '}
                <strong>{contact.name}</strong>
                {contact.contact_type ? <> ({contact.contact_type})</> : null}.
              </p>

              {loading && (
                <div className="text-muted-foreground flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Checking historical records…
                </div>
              )}

              {!loading && impact && !isRestore && (
                <div className="rounded-lg border bg-muted/40 p-3 space-y-1.5" data-testid="impact-summary">
                  <p className="font-medium text-xs uppercase tracking-wide text-muted-foreground">
                    What&apos;s attached to this contact
                  </p>
                  <div className="flex items-center justify-between text-sm">
                    <span>Open invoices</span>
                    <Badge variant={impact.open_invoices ? 'destructive' : 'secondary'}>
                      {impact.open_invoices}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span>Total invoices (incl. paid)</span>
                    <Badge variant="secondary">{impact.total_invoices}</Badge>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span>Open bills</span>
                    <Badge variant={impact.open_bills ? 'destructive' : 'secondary'}>
                      {impact.open_bills}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span>Total bills (incl. paid)</span>
                    <Badge variant="secondary">{impact.total_bills}</Badge>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span>Notes &amp; contacts attached</span>
                    <Badge variant="secondary">{impact.total_notes + impact.total_people}</Badge>
                  </div>
                </div>
              )}

              {isArchive && (
                <div className="rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200/40 p-3 text-amber-900 dark:text-amber-200 text-xs">
                  ✓ Archiving is reversible — the contact is hidden from default views
                  but all invoices, bills, notes, and history are preserved. You can
                  restore them from the <strong>Archived</strong> tab.
                </div>
              )}

              {isRestore && (
                <div className="rounded-md bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200/40 p-3 text-emerald-900 dark:text-emerald-200 text-xs">
                  This contact will return to your active list with all its history intact.
                </div>
              )}

              {isDelete && (
                <>
                  <div className="rounded-md bg-rose-50 dark:bg-rose-950/30 border border-rose-200/40 p-3 space-y-2 text-rose-900 dark:text-rose-200 text-xs">
                    <p className="font-semibold flex items-center gap-1.5">
                      <AlertTriangle className="w-4 h-4" /> This action is irreversible.
                    </p>
                    <ul className="list-disc pl-5 space-y-1">
                      <li>The contact record is removed from the database.</li>
                      <li>Notes, attached people, and custom fields are deleted.</li>
                      <li>Any audit-log entries pointing at this contact will become orphans.</li>
                      <li>If you only want to hide them, <strong>Archive</strong> instead.</li>
                    </ul>
                  </div>

                  {impact && impact.blockers?.includes('historical_invoices_or_bills') && (
                    <div className="rounded-md bg-zinc-100 dark:bg-zinc-900 border border-zinc-300/50 p-3 text-xs">
                      ⚠️ This contact has historical invoices or bills. Permanent delete
                      is blocked to preserve your accounting history — please archive instead.
                    </div>
                  )}

                  {!viewerIsAdmin && (
                    <div className="rounded-md bg-zinc-100 dark:bg-zinc-900 border border-zinc-300/50 p-3 text-xs flex items-center gap-2">
                      <ShieldAlert className="w-4 h-4 text-rose-500" />
                      Only workspace owners and admins can permanently delete contacts.
                    </div>
                  )}

                  {viewerIsAdmin && impact?.can_hard_delete && (
                    <div>
                      <label className="block text-xs font-medium mb-1.5">
                        Type <code className="px-1 py-0.5 rounded bg-muted">{contact.name}</code> to confirm:
                      </label>
                      <Input
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        placeholder={contact.name}
                        autoFocus
                        data-testid="confirm-delete-input"
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`${mode}-cancel-btn`}>Cancel</AlertDialogCancel>
          <Button
            onClick={handleConfirm}
            disabled={!canConfirm || submitting || loading}
            variant={isDelete ? 'destructive' : 'default'}
            data-testid={`${mode}-confirm-btn`}
            className={
              isArchive ? 'bg-amber-500 hover:bg-amber-600 text-white' :
              isRestore ? 'bg-emerald-500 hover:bg-emerald-600 text-white' :
              undefined
            }
          >
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {isDelete && <Trash2 className="w-4 h-4 mr-2" />}
            {isArchive && <Archive className="w-4 h-4 mr-2" />}
            {isRestore && <RotateCcw className="w-4 h-4 mr-2" />}
            {isDelete ? 'Permanently delete' : isArchive ? 'Archive contact' : 'Restore contact'}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
